import clsx from 'clsx';
import Button from 'components/atoms/Button/Button';
import Card from 'components/atoms/Card/Card';
import Collapsible from 'components/atoms/Collapsible/Collapsible';
import DateTime from 'components/atoms/DateTime/DateTime';
import Heading from 'components/atoms/Heading/Heading';
import Txt from 'components/atoms/Txt/Txt';
import React, { useCallback, useEffect, useRef, useTransition } from 'react';
import { usePaginationFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { TransactionHistoryFragment$key } from './__generated__/TransactionHistoryFragment.graphql';
import classes from './TransactionHistory.module.scss';

const TransactionHistoryFragment = graphql`
  fragment TransactionHistoryFragment on Query
  @refetchable(queryName: "TransactionHistoryFragmentRefetchQuery")
  @argumentDefinitions(cursor: { type: "String" }, count: { type: "Int", defaultValue: 20 }) {
    walletTransactions(after: $cursor, first: $count)
      @connection(key: "TransactionHistoryFragment_walletTransactions") {
      edges {
        node {
          id
          amount {
            formatted
          }
          createdAt
          eventType
          sourceType
          description
          supportId
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`;

function formatDescription(description: string): string {
  switch (description) {
    case 'charity':
      return 'Charity';
    case 'gift_card':
      return 'Gift Card';
    case 'paypal':
      return 'PayPal';
    default:
      return description;
  }
}

export interface Props {
  transactionsRef: TransactionHistoryFragment$key;
  isCollapsible?: boolean;
}

export default function TransactionHistory({ transactionsRef, isCollapsible }: Props) {
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  const [isPending, startTransition] = useTransition();
  const { data, loadNext, isLoadingNext } = usePaginationFragment(
    TransactionHistoryFragment,
    transactionsRef,
  );
  const onLoadMore = () =>
    startTransition(() => {
      loadNext(20);
    });

  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      if (
        isFiltersOpen &&
        e.target !== filtersRef.current &&
        !filtersRef.current?.contains(e.target as Node)
      ) {
        setIsFiltersOpen(false);
      }
    },
    [isFiltersOpen],
  );

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const content = (
    <>
      <section className={clsx(classes.listingSection)}>
        <table className={clsx(classes.listingTable)}>
          <tbody>
            {data.walletTransactions && data.walletTransactions.edges.length > 0 ? (
              data.walletTransactions.edges.map((edge) => (
                <tr key={edge.node.id} className={classes.transactionRow}>
                  <td className={classes.transactionTitleColumn}>
                    <Txt skipTranslation component={'strong'} className={classes.transactionTitle}>
                      {formatDescription(edge.node.description)}
                    </Txt>
                    <DateTime
                      className={classes.transactionDate}
                      dateTime={edge.node.createdAt}
                      dateStyle={'medium'}
                      timeStyle={'short'}
                    />
                  </td>
                  <td className={classes.transactionAmount}>{edge.node.amount.formatted}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} style={{ textAlign: 'center', fontSize: '1.1rem' }}>
                  <Txt>You have no rewards activity at this time.</Txt>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <hr className={clsx(classes.divider)} />
      </section>
      <section className={clsx(classes.footerSection)}>
        {data.walletTransactions?.pageInfo.hasNextPage && (
          <Button
            buttonSize={'normal'}
            buttonType={'tertiary'}
            onClick={onLoadMore}
            disabled={isLoadingNext}
            className={classes.loadMoreButton}
          >
            <Txt>view more transactions</Txt>
          </Button>
        )}
      </section>
    </>
  );

  return (
    <>
      {isPending && <Txt>Loading</Txt>}
      <div className={clsx(classes.root)}>
        {isCollapsible ? (
          <Collapsible
            className={classes.collapsible}
            headerClassName={classes.collapsibleHeader}
            title={`Transaction History`}
            startOpen
            openTitle={
              <Heading level={5} className={classes.transactionHeading}>
                Transaction History
              </Heading>
            }
            closedTitle={
              <Heading level={5} className={classes.transactionHeading}>
                Transaction History
              </Heading>
            }
          >
            {content}
          </Collapsible>
        ) : (
          content
        )}
      </div>
    </>
  );
}
